<template>
  <div class="ve-split">
    <!-- Left: list of deployments -->
    <div class="ve-list-panel">
      <div class="ve-list-header">
        <span class="ve-list-title">{{ selectedDeployments.length }} deployment{{ selectedDeployments.length === 1 ? '' : 's' }}</span>
        <button type="button" class="ve-add-btn" :class="{ 've-add-btn--active': mode === 'add' }" @click="startAdd">+ Add</button>
      </div>
      <div class="ve-list-body">
        <div v-if="selectedDeployments.length === 0" class="ve-list-empty">No deployments yet</div>
        <div
          v-for="(dep, idx) in selectedDeployments"
          :key="dep.uid"
          class="ve-list-item"
          :class="{ 've-list-item--selected': mode === 'edit' && selectedIdx === idx }"
          @click="selectItem(idx)"
        >
          <img :src="'/Images/Deployments/' + dep.img" class="ve-list-thumb" :alt="dep.img" @error="$event.target.style.opacity='0'" />
          <span class="ve-list-name">{{ dep.name }}</span>
          <button type="button" class="ve-list-remove" title="Remove" @click.stop="removeDeployment(dep.uid)">✕</button>
        </div>
      </div>
    </div>

    <!-- Right: image grid (add) or edit fields (edit) -->
    <div class="ve-detail-panel">
      <!-- ADD MODE -->
      <template v-if="mode === 'add'">
        <p class="ve-hint">Click a deployment layout to add it to the list.</p>
        <div class="dep-image-grid">
          <div
            v-for="img in deploymentImages"
            :key="img"
            class="dep-image-option"
            @click="addDeployment(img)"
          >
            <img :src="'/Images/Deployments/' + img" :alt="img" class="dep-image-thumb" />
          </div>
        </div>
      </template>

      <!-- EDIT MODE -->
      <template v-else-if="mode === 'edit' && selectedDep">
        <div class="ve-edit-form">
          <div class="ve-edit-row-head">
            <img :src="'/Images/Deployments/' + selectedDep.img" class="ve-edit-thumb-wide" />
            <div>
              <div class="ve-edit-skin-name">Deployment layout {{ parseInt(selectedDep.img) }}</div>
              <div class="ve-edit-hint">Deployment {{ selectedIdx + 1 }} of {{ selectedDeployments.length }}</div>
            </div>
          </div>
          <div class="ve-edit-group">
            <label class="ve-edit-label">Name</label>
            <input v-model="selectedDep.name" type="text" class="field-input" @input="emitChange" />
          </div>
          <div class="ve-edit-group">
            <label class="ve-edit-label">X</label>
            <input v-model.number="selectedDep.x" type="number" step="1" class="field-input" @input="emitChange" />
          </div>
          <div class="ve-edit-group">
            <label class="ve-edit-label">Y</label>
            <input v-model.number="selectedDep.y" type="number" step="1" class="field-input" @input="emitChange" />
          </div>
          <div class="ve-edit-group">
            <label class="ve-edit-label">Z</label>
            <input v-model.number="selectedDep.z" type="number" step="1" class="field-input" @input="emitChange" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeploymentsEditor',

  props: {
    value: { type: Array, default: () => [] },
  },

  data() {
    return {
      selectedDeployments: [],
      deploymentUidCounter: 0,
      mode: 'add',
      selectedIdx: null,
    };
  },

  computed: {
    deploymentImages() {
      return Array.from({ length: 14 }, (_, i) => `${i}.jpg`);
    },
    selectedDep() {
      if (this.selectedIdx === null) return null;
      return this.selectedDeployments[this.selectedIdx] || null;
    },
  },

  mounted() {
    if (this.value && this.value.length > 0) {
      this.initFromValue(this.value);
    }
  },

  watch: {
    value(newVal) {
      this.initFromValue(newVal);
    },
  },

  methods: {
    startAdd() {
      this.mode = 'add';
      this.selectedIdx = null;
    },

    selectItem(idx) {
      this.mode = 'edit';
      this.selectedIdx = idx;
    },

    initFromValue(deployments) {
      this.deploymentUidCounter = 0;
      this.selectedDeployments = (deployments || []).map(dep => ({
        uid: this.deploymentUidCounter++,
        img: dep.deploymentID + '.jpg',
        name: dep.deploymentName || ('Deployment ' + this.deploymentUidCounter),
        x: dep.X || 0,
        y: dep.Y || 0,
        z: dep.Z || 0,
      }));
    },

    addDeployment(img) {
      const uid = this.deploymentUidCounter++;
      const defaultName = 'Deployment ' + (this.selectedDeployments.length + 1);
      this.selectedDeployments.push({ uid, img, name: defaultName, x: 0, y: 0, z: 0 });
      // Auto-switch to edit mode for the new entry
      this.mode = 'edit';
      this.selectedIdx = this.selectedDeployments.length - 1;
      this.emitChange();
    },

    removeDeployment(uid) {
      const idx = this.selectedDeployments.findIndex(d => d.uid === uid);
      if (idx >= 0) {
        if (this.mode === 'edit' && this.selectedIdx === idx) {
          this.mode = 'add';
          this.selectedIdx = null;
        } else if (this.mode === 'edit' && this.selectedIdx > idx) {
          this.selectedIdx--;
        }
        this.selectedDeployments.splice(idx, 1);
        if (this.selectedDeployments.length === 0) {
          this.mode = 'add';
          this.selectedIdx = null;
        }
      }
      this.emitChange();
    },

    emitChange() {
      this.$emit('input', this.buildDeploymentsJson());
    },

    buildDeploymentsJson() {
      return this.selectedDeployments.map(dep => {
        const entry = { deploymentID: parseInt(dep.img), deploymentName: dep.name };
        if (dep.x) entry.X = dep.x;
        if (dep.y) entry.Y = dep.y;
        if (dep.z) entry.Z = dep.z;
        return entry;
      });
    },
  },
};
</script>

<style>
.dep-image-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 0.6rem; margin-top: 0.5rem; }
.dep-image-option { position: relative; border: 2px solid #30363d; border-radius: 6px; cursor: pointer; overflow: hidden; transition: border-color 0.15s; }
.dep-image-option:hover { border-color: #58a6ff; }
.dep-image-thumb { width: 100%; aspect-ratio: 4/3; object-fit: cover; display: block; }
</style>
